import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { Permission } from '../enums/permission.enum';
import { RbacService } from '../services/rbac.service';
import {
  selectActiveDomainAccess,
  selectUser,
} from '../../../store/entities/user/user.selectors';

// During the login handshake this guard can run before /me and the current
// domain have resolved. rbac.can() reads both the user record and
// activeDomainAccess — wait for both before judging. Timeout covers flows
// where those never arrive (e.g. an unauthenticated deep link while the
// login redirect departs).
const USER_LOAD_TIMEOUT_MS = 15000;

export function permissionGuard(permission: Permission): CanActivateFn {
  return () => {
    const rbac = inject(RbacService);
    const router = inject(Router);
    const store = inject(Store);

    const decide = () =>
      rbac.can(permission) ? true : router.createUrlTree(['/maintenance/403']);

    return combineLatest([
      store.select(selectUser),
      store.select(selectActiveDomainAccess),
    ]).pipe(
      filter(([user, domainAccess]) => user !== null && domainAccess !== null),
      take(1),
      map(decide),
      timeout(USER_LOAD_TIMEOUT_MS),
      catchError(() => of(decide()))
    );
  };
}
